/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-type is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

// NewUser page components
import FormField from "modules/inspections/components/FormField";
import { useEffect, useState } from "react";
import { Box, Card, Switch } from "@mui/material";
import { SyncLoader } from 'react-spinners';
import SoftSelect from "components/SoftComponents/SoftSelect";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftButton from "components/SoftComponents/SoftButton";
import Divider from '@material-ui/core/Divider';

import { actionItem } from "modules/inspections/utils/technician_actions";
import { filterOptions, calcDistanceSuffix } from "modules/inspections/utils/data";
import { distance, point } from "@turf/turf";
import DeviceSelectionCard from "components/Cards/DeviceSelectionCard";

function checkActionsValues(values) {
    var normal = (
        values['devicePrimary-actions-removed'] ||
        values['devicePrimary-actions-installed'] ||
        values['devicePrimary-actions-cleaned'] ||
        values['devicePrimary-actions-battery_swap'] ||
        values['devicePrimary-actions-hardware_changed'] ||
        values['devicePrimary-actions-cosmetically_fixed']
    );

    return normal;
}

const checkWorks = (values, actionsOther, setFieldValue, inspection_type) => {
    const hasStandardAction = checkActionsValues(values);

    const otherFieldHasText = values['devicePrimary-actions-other']?.trim().length > 0;
    const secondaryDeviceContent = values['deviceSecondary-uuid']?.trim().length > 0;

    if (values['devicePrimary-actions-removed']) {
        setFieldValue(inspection_type.name, secondaryDeviceContent ? "Unit Replacement" : "Unit Decommission");
    }
    else {
        setFieldValue(inspection_type.name, "Maintenance Inspection");
    }

    if (actionsOther) {
        return otherFieldHasText && !secondaryDeviceContent;
    }

    return hasStandardAction && !secondaryDeviceContent;
}

function TechnicianActions({ formData, deviceLayout }) {
    const {
        values, touched, errors, formField, isSubmitting,
        functions: { setDeviceOptions, setFieldValue, handleChange, handleBack, handleCancel },
        custom: { activeStep, isLastStep, user, all_devices, deviceOptions }
    } = formData;
    const { devicePrimary, deviceSecondary, inspection_type } = formField;

    const [makeActions, setMakeActions] = useState(checkActionsValues(values) || false);
    const [works, setWorks] = useState(false);
    const [currentLocation] = useState(values.location || null);
    const [removedState, setRemovedState] = useState(values?.['devicePrimary-actions-removed'] || false);
    const [actionsOther, setActionsOther] = useState(false);

    useEffect(() => {
        setWorks(checkWorks(values, actionsOther, setFieldValue, inspection_type));
        if (!removedState) {
            handleClick(null);
        }
    }, [values, actionsOther]);

    useEffect(() => {
        const origin = point(currentLocation);
        const options = {
            units: "metres"
        };

        function locationSort(a, b) {
            const pointA = point([a.position[1], a.position[0]]);
            const pointB = point([b.position[1], b.position[0]]);

            const distanceA = distance(origin, pointA, options);
            const distanceB = distance(origin, pointB, options);

            return distanceA - distanceB;
        }

        const device_options = all_devices.sort(locationSort).filter((row) => row.uuid != values['devicePrimary-uuid']).map((device) => {
            return {
                metadata: device,
                value: {
                    name: device.name,
                    serial_number: device.serial_number,
                    stop_id: device.stop?.stop_id ? device.stop.stop_id : device.stop.site_id,
                    uuid: device.uuid,
                    distance_away: distance(origin, point([device.position[1], device.position[0]]), options)
                },
                label: device.name
            }
        });

        setDeviceOptions(device_options);
    }, [all_devices]);

    const handleClick = (selected, e = false) => {
        if (e) e.stopPropagation();
        setFieldValue(deviceSecondary.info.name, selected ? selected : "");
        setFieldValue(deviceSecondary.uuid.name, selected ? selected.value.uuid : "");
        setFieldValue(deviceSecondary.stop_id.name, selected ? selected.value.stop_id : "");
        setFieldValue(deviceSecondary.serial_no.name, selected ? selected.value.serial_number : "");
        setFieldValue(deviceSecondary.actions.installed.name, selected ? true : false);
        setFieldValue(deviceSecondary.actions.cleaned.name, selected ? true : false);
    }

    const resetActions = (voided = false) => {
        var action_types = ['removed', 'installed', 'cleaned', 'battery_swap', 'hardware_changed', 'cosmetically_fixed', 'other'];
        if (voided) action_types = action_types.filter((row) => row != voided);

        for (var type of action_types) {
            setFieldValue(devicePrimary.actions[type].name, type == 'other' ? "" : false);
        }
    }

    return (
        <SoftBox>
            <SoftBox>
                <SoftBox mb={3} display="flex" flexDirection="column" alignItems="center">
                    <SoftTypography variant="h4" fontWeight="bold">
                        Maintenance / Repairs
                    </SoftTypography>
                    <SoftTypography pt={1} variant="mobileBody1" textAlign="center">
                        Has there been any works performed?
                    </SoftTypography>
                </SoftBox>
                <SoftBox mt={3}>
                    <Grid container spacing={1}>
                        <Grid item xxs={1} md={3} xl={4} />
                        <Grid item xxs={5} md={3} xl={2}>
                            <SoftButton color="dark" variant="gradient" size="medium" sx={{ width: '100% !important' }} onClick={(e) => { setFieldValue(inspection_type.name, "General Inspection"); resetActions(); }} disabled={isSubmitting} type="submit">
                                No
                            </SoftButton>
                        </Grid>
                        <Grid item xxs={5} md={3} xl={2}>
                            <SoftButton color={makeActions ? "secondary" : "dark"} variant="gradient" size="medium" sx={{ width: '100% !important' }} onClick={(e) => { setMakeActions(true); setFieldValue(inspection_type.name, "Maintenance Inspection") }}>
                                Yes
                            </SoftButton>
                        </Grid>
                        <Grid item xxs={1} md={3} xl={4} />
                    </Grid>
                </SoftBox>

                {makeActions && (
                    <SoftBox mt={3}>
                        <Divider />
                        <SoftBox mt={2}>
                            <Box display="flex" flex={1} flexDirection={{ xxs: 'column', xl: 'row' }}>
                                <Box flex={{ xxs: 12, xl: 6 }}>
                                    <SoftBox pb={1}>
                                        <SoftTypography variant={deviceLayout == "mobile" ? "mobileBody1" : "subtitle2"} fontWeight={deviceLayout == "mobile" ? "bold" : "regular"}>
                                            Please select the rectifications you have made, or select 'Other' to add another.
                                        </SoftTypography>
                                    </SoftBox>
                                    <Grid container spacing={1}>
                                        {actionItem('inspection', 'removed', 'Removed/Replaced', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                        {actionItem('inspection', 'installed', 'Installed', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                        {actionItem('inspection', 'cleaned', 'Cleaned', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                        {actionItem('inspection', 'battery_swap', 'Battery Swap', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                        {actionItem('inspection', 'hardware_changed', 'Hardware Changed', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                        {actionItem('inspection', 'cosmetically_fixed', 'Cosmetically Fixed', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                        <Grid item xxs={12}>
                                            <SoftBox>
                                                <SoftBox display="flex" justifyContent="center" py={2} px={2}
                                                    onClick={(e) => {
                                                        setActionsOther(!actionsOther);
                                                    }}
                                                    sx={({ palette: { gradients }, functions: { rgba, linearGradient } }) => ({
                                                        borderRadius: '10px',
                                                        cursor: 'pointer',
                                                        background: linearGradient(rgba(gradients[(actionsOther ? 'secondary' : 'light')].main, 0.6), rgba(gradients[(actionsOther ? 'secondary' : 'light')].state, 0.6)),
                                                        border: '1px solid #aaa'
                                                    })}>
                                                    <SoftBox display="flex" alignItems="center" sx={{ paddingTop: '2.5px' }}>
                                                        <SoftTypography variant={deviceLayout == "mobile" ? "mobileBody1" : "body2"} fontWeight={'bold'}>Other</SoftTypography>
                                                    </SoftBox>
                                                </SoftBox>
                                                {actionsOther && (
                                                    <SoftBox>
                                                        <FormField
                                                            type={devicePrimary.actions.other.type}
                                                            label={devicePrimary.actions.other.label}
                                                            name={devicePrimary.actions.other.name}
                                                            value={values['devicePrimary-actions-other']}
                                                            placeholder={devicePrimary.actions.other.placeholder}
                                                            // error={values['devicePrimary-actions-other'].length < 1}
                                                            // success={values['devicePrimary-actions-other'].length > 0 && !errors['devicePrimary-actions-other']}
                                                            onChange={handleChange}
                                                        />
                                                    </SoftBox>
                                                )}
                                            </SoftBox>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>
                        </SoftBox>
                    </SoftBox>
                )}
            </SoftBox>
            {removedState && (
                <SoftBox mt={4}>
                    <Grid container spacing={3}>
                        <Grid item xxs={12} lg={6}>
                            <SoftBox display="flex" flexDirection="column" alignItems={{ xxs: "center", lg: 'flex-start' }}>
                                <SoftTypography variant="h4" fontWeight="bold" textAlign={{ xxs: "center", lg: "left" }}>
                                    Replacement Search
                                </SoftTypography>
                            </SoftBox>
                            <SoftBox mb={1} display="flex" flexDirection="column" alignItems={{ xxs: "center", lg: 'flex-start' }}>
                                <SoftTypography pt={1} variant="mobileBody1" textAlign={{ xxs: "center", lg: "left" }}>
                                    If there is a <b>replacement</b> device, type to search by:<br /><b>Device Name/Stop Name</b>, <b>Stop ID</b>, <b>Serial Number</b>, or <b>Site ID</b>
                                </SoftTypography>
                            </SoftBox>
                            <SoftBox mb={2} sx={({ palette: { white }, borders: { borderRadius } }) => ({ maxWidth: '100%', borderRadius: borderRadius.md, backgroundColor: white.main })}>
                                <SoftSelect
                                    name={deviceSecondary.info.name}
                                    value={deviceOptions.find(opt => opt.label === values['deviceSecondary-info']?.label) || ""}
                                    onChange={(selected) => handleClick(selected)}
                                    filterOption={filterOptions}
                                    options={deviceOptions}
                                    isClearable={true}
                                    isSearchable={true}
                                    placeholder="Search or select a device..."

                                    error={errors.deviceSecondary?.info && touched.deviceSecondary?.info}
                                />
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={12} lg={6}>
                            {typeof values['deviceSecondary-info'] != 'string' && (
                                <DeviceSelectionCard
                                    title={values['deviceSecondary-info']?.label}
                                    description={[`Stop ID: ${values['deviceSecondary-stop_id']}`, `Serial No.: ${values['deviceSecondary-serial_no']}`]}
                                    action={{ func: isSubmitting, type: "submit", label: "select" }}
                                    value={calcDistanceSuffix(values['deviceSecondary-info']?.value.distance_away)}
                                />
                            )}
                        </Grid>
                    </Grid>
                </SoftBox>
            )}
            <SoftBox mt={5} width="100%" display="flex" justifyContent="space-between">
                <SoftButton variant="gradient" color={activeStep === 0 ? "error" : "light"} onClick={activeStep === 0 ? handleCancel : handleBack}>
                    {activeStep === 0 ? "cancel" : "back"}
                </SoftButton>
                {(makeActions && works) && (
                    <SoftButton
                        disabled={isSubmitting}
                        type="submit"
                        variant="gradient"
                        color="dark"
                    >
                        {isLastStep ? "submit" : "next"}
                    </SoftButton>
                )}
            </SoftBox>
        </SoftBox>
    );
}

// typechecking props for TechnicianActions
TechnicianActions.propTypes = {
    formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
    deviceLayout: PropTypes.string,
};

export default TechnicianActions;
